import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DoCheck,
  effect,
  ElementRef,
  inject,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  computed,
  untracked,
  input,
  output,
  viewChild,
} from '@angular/core';
import { DynamicViewDirective, isComponent, isTemplateRef } from '@ngneat/overview';

import { ENTER_ANIMATION_DURATION, EXIT_ANIMATION_DURATION, HOT_TOAST_DEPTH_SCALE } from '../../constants';
import { HotToastRef } from '../../hot-toast-ref';
import { CreateHotToastRef, HotToastClose, HotToastGroupEvent, Toast, ToastConfig } from '../../hot-toast.model';
import { animate } from '../../utils';
import { IndicatorComponent } from '../indicator/indicator.component';
import { AnimatedIconComponent } from '../animated-icon/animated-icon.component';
import { HotToastGroupItemComponent } from '../hot-toast-group-item/hot-toast-group-item.component';

@Component({
  selector: 'hot-toast-component',
  templateUrl: 'hot-toast.component.html',

  imports: [DynamicViewDirective, IndicatorComponent, AnimatedIconComponent, HotToastGroupItemComponent],
})
export class HotToastComponent implements OnInit, AfterViewInit, OnDestroy, DoCheck {
  readonly toast = input.required<Toast<unknown>>();
  readonly offset = input(0);
  readonly defaultConfig = input<ToastConfig>();
  readonly toastRef = input<CreateHotToastRef<unknown>>();
  readonly toastsAfter = input(0);

  readonly isShowingAllToasts = input(false);

  readonly height = output<number>();
  readonly beforeClosed = output();
  readonly afterClosed = output<HotToastClose>();
  readonly showAllToasts = output<boolean>();
  readonly toggleGroup = output<HotToastGroupEvent>();

  private readonly toastBarBase = viewChild.required<ElementRef<HTMLElement>>('hotToastBarBase');

  isManualClose = false;
  context!: Record<string, unknown>;
  toastComponentInjector!: Injector;
  isExpanded = false;
  toastBarBaseStylesSignal = signal<Record<string, string>>({});

  private unlisteners: VoidFunction[] = [];
  private rafIds: number[] = [];
  private destroyed = false;
  private softClosed = false;
  private groupRefs: CreateHotToastRef<unknown>[] = [];

  private injector = inject(Injector);
  private renderer = inject(Renderer2);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);

  private isFirstToastChange = true;

  constructor() {
    // Replaces the former `toast` setter side effect: recompute the base styles whenever the toast input changes.
    effect(() => {
      const value = this.toast();
      // Read the current style without tracking it, otherwise this effect would re-run on its own writes.
      const ogStyle = untracked(() => this.toastBarBaseStylesSignal());
      const newStyle: Record<string, string> = { ...value.style };

      if (ogStyle['animation']?.includes('hotToastExitAnimation')) {
        // if toast is set for exit, we don't need want set the enter animation
        newStyle['animation'] = ogStyle['animation'];
      } else {
        const top = value.position?.includes('top');
        const enterAnimation = `hotToastEnterAnimation${
          top ? 'Negative' : 'Positive'
        } ${ENTER_ANIMATION_DURATION}ms cubic-bezier(0.21, 1.02, 0.73, 1) forwards`;
        newStyle['animation'] = enterAnimation;
      }

      untracked(() => this.toastBarBaseStylesSignal.set(newStyle));
    });

    // Replaces the former `ngOnChanges` handler: signal inputs do not trigger `ngOnChanges`,
    // so re-emit the height on every toast change after the first one.
    effect(() => {
      const value = this.toast();
      if (this.isFirstToastChange) {
        this.isFirstToastChange = false;
        return;
      }
      if (value?.message) {
        untracked(() => this.emiHeightWithGroup(this.isExpanded));
      }
    });

    // Replaces the former `toastsAfter` setter side effect.
    effect(() => {
      const value = this.toastsAfter();
      untracked(() => {
        const defaultConfig = this.defaultConfig();
        if ((defaultConfig?.visibleToasts ?? 0) > 0) {
          if (this.toast().autoClose) {
            // if (value >= this.defaultConfig?.visibleToasts) {
            //   this.close();
            // }
          } else {
            if (value >= (defaultConfig?.visibleToasts ?? 0)) {
              this.softClose();
            } else if (this.softClosed) {
              this.softOpen();
            }
          }
        }
      });
    });
  }

  readonly message = computed(() => this.toast().message);
  readonly nativeElement = computed(() => this.toastBarBase().nativeElement);

  readonly scale = computed(() =>
    this.defaultConfig()?.stacking !== 'vertical' && !this.isShowingAllToasts()
      ? this.toastsAfter() * -HOT_TOAST_DEPTH_SCALE + 1
      : 1,
  );

  readonly translateY = computed(() => this.offset() * (this.top() ? 1 : -1) + 'px');

  readonly exitAnimationDelay = computed(() => this.toast().duration + 'ms');

  readonly top = computed(() => this.toast().position?.includes('top'));

  readonly containerPositionStyle = computed(() => {
    const verticalStyle = this.top() ? { top: 0 } : { bottom: 0 };
    const transform = `translateY(var(--hot-toast-translate-y)) scale(var(--hot-toast-scale))`;

    const horizontalStyle = this.toast().position?.includes('left')
      ? { left: 0 }
      : this.toast().position?.includes('right')
        ? {
            right: 0,
          }
        : {
            left: 0,
            right: 0,
            justifyContent: 'center',
          };
    return {
      transform,
      ...verticalStyle,
      ...horizontalStyle,
    };
  });

  readonly isIconString = computed(() => typeof this.toast().icon === 'string');

  get groupChildrenToastRefs() {
    return this.groupRefs.filter((ref) => !!ref);
  }
  set groupChildrenToastRefs(value: CreateHotToastRef<unknown>[]) {
    this.groupRefs = value;

    (this.toastRef() as { groupRefs: CreateHotToastRef<unknown>[] }).groupRefs = value;
  }

  // Note: not a `computed()` — `groupChildrenToastRefs` above reads the plain `groupRefs` field
  // (kept in sync imperatively in `ngDoCheck`, not a signal), so a memoized computed() here would
  // never invalidate when the group's children change.
  get groupChildrenToasts() {
    return this.groupChildrenToastRefs.map((ref) => ref.getToast());
  }

  get groupHeight() {
    return this.visibleToasts
      .slice(-(this.defaultConfig()?.visibleToasts ?? 0))
      .map((t) => t.height)
      .reduce((prev, curr) => (prev || 0) + (curr || 0), 0);
  }

  get visibleToasts() {
    return this.groupChildrenToasts.filter((t) => t.visible);
  }

  ngDoCheck() {
    const toastRef = this.toastRef();
    if (toastRef?.groupRefs.length !== this.groupRefs.length) {
      if (toastRef) {
        this.groupRefs = toastRef.groupRefs.slice();
      }
      this.cdr.markForCheck();

      this.emiHeightWithGroup(this.isExpanded);
    }
    if (toastRef?.groupExpanded !== this.isExpanded) {
      this.isExpanded = toastRef?.groupExpanded ?? false;
      this.cdr.markForCheck();

      this.emiHeightWithGroup(this.isExpanded);
    }
  }

  ngOnInit() {
    if (isTemplateRef(this.message())) {
      this.context = { $implicit: this.toastRef() };
    }
    if (isComponent(this.message())) {
      this.toastComponentInjector = Injector.create({
        providers: [
          {
            provide: HotToastRef,
            useValue: this.toastRef(),
          },
        ],
        parent: this.toast().injector || this.injector,
      });
    }

    const nativeElement = this.nativeElement();
    // Caretaker note: `animationstart` and `animationend` events are event tasks that trigger change detection.
    // We'd want to trigger the change detection only if it's an exit animation.
    this.ngZone.runOutsideAngular(() => {
      this.unlisteners.push(
        // Caretaker note: we have to remove these event listeners at the end (even if the element is removed from DOM).
        this.renderer.listen(nativeElement, 'animationstart', (event: AnimationEvent) => {
          if (this.isExitAnimation(event)) {
            this.ngZone.run(() => {
              this.renderer.setStyle(nativeElement, 'pointer-events', 'none');
              this.renderer.setStyle(nativeElement.parentElement, 'pointer-events', 'none');
              this.beforeClosed.emit();
            });
          }
        }),
        this.renderer.listen(nativeElement, 'animationend', (event: AnimationEvent) => {
          if (this.isEnterAnimation(event)) {
            this.ngZone.run(() => {
              if (this.toast().autoClose) {
                const exitAnimation = `hotToastExitAnimation${
                  this.top() ? 'Negative' : 'Positive'
                } ${EXIT_ANIMATION_DURATION}ms forwards cubic-bezier(0.06, 0.71, 0.55, 1) var(--hot-toast-exit-animation-delay) var(--hot-toast-exit-animation-state)`;
                this.toastBarBaseStylesSignal.set({ ...this.toast().style, animation: exitAnimation });
              }
            });
          }
          if (this.isExitAnimation(event)) {
            this.ngZone.run(() =>
              this.afterClosed.emit({ dismissedByAction: this.isManualClose, id: this.toast().id }),
            );
          }
        }),
      );
    });
  }

  ngAfterViewInit() {
    const nativeElement = this.nativeElement();
    // Caretaker note: accessing `offsetHeight` triggers the whole layout update.
    // Macro tasks (like `setTimeout`) might be executed within the current rendering frame and cause a frame drop.
    const rafId = requestAnimationFrame(() => {
      this.rafIds = this.rafIds.filter((id) => id !== rafId);
      if (this.destroyed) return;
      this.height.emit(nativeElement.offsetHeight);
    });
    this.rafIds.push(rafId);

    this.setToastAttributes();
  }

  softClose() {
    const exitAnimation = `hotToastExitSoftAnimation${
      this.top() ? 'Negative' : 'Positive'
    } ${EXIT_ANIMATION_DURATION}ms forwards cubic-bezier(0.06, 0.71, 0.55, 1)`;

    const nativeElement = this.nativeElement();

    animate(this.renderer, nativeElement, exitAnimation);
    this.softClosed = true;

    if (this.isExpanded) {
      this.toggleToastGroup();
    }
  }

  softOpen() {
    const softEnterAnimation = `hotToastEnterSoftAnimation${
      this.top() ? 'Negative' : 'Positive'
    } ${ENTER_ANIMATION_DURATION}ms cubic-bezier(0.21, 1.02, 0.73, 1) forwards`;

    const nativeElement = this.nativeElement();

    animate(this.renderer, nativeElement, softEnterAnimation);
    this.softClosed = false;
  }

  close() {
    this.isManualClose = true;
    this.cdr.markForCheck();

    const exitAnimation = `hotToastExitAnimation${
      this.top() ? 'Negative' : 'Positive'
    } ${EXIT_ANIMATION_DURATION}ms forwards cubic-bezier(0.06, 0.71, 0.55, 1)`;

    this.toastBarBaseStylesSignal.set({ ...this.toast().style, animation: exitAnimation });
  }

  handleMouseEnter() {
    this.showAllToasts.emit(true);
  }
  handleMouseLeave() {
    this.showAllToasts.emit(false);
  }

  ngOnDestroy() {
    this.destroyed = true;
    while (this.rafIds.length) {
      cancelAnimationFrame(this.rafIds.pop() || 0);
    }
    this.close();
    while (this.unlisteners.length) {
      this.unlisteners.pop()?.();
    }
  }

  private isExitAnimation(ev: AnimationEvent) {
    return ev.animationName.includes('hotToastExitAnimation');
  }

  private isEnterAnimation(ev: AnimationEvent) {
    return ev.animationName.includes('hotToastEnterAnimation');
  }

  private setToastAttributes() {
    const toastAttributes: Record<string, string> = this.toast().attributes || {};
    for (const [key, value] of Object.entries(toastAttributes)) {
      this.renderer.setAttribute(this.nativeElement(), key, value);
    }
  }

  calculateOffset(toastId: string) {
    const visibleToasts = this.visibleToasts;
    const index = visibleToasts.findIndex((toast) => toast.id === toastId);
    const offset =
      index !== -1
        ? visibleToasts
            .slice(...(this.defaultConfig()?.reverseOrder ? [index + 1] : [0, index]))
            .reduce((acc, t, i) => {
              const defaultConfig = this.defaultConfig();
              return defaultConfig?.visibleToasts !== 0 &&
                i < visibleToasts.length - (defaultConfig?.visibleToasts ?? 0)
                ? 0
                : acc + (t.height || 0);
            }, 0)
        : 0;
    return offset;
  }

  updateHeight(height: number, toast: Toast<unknown>) {
    toast.height = height;
    this.cdr.markForCheck();
  }

  beforeClosedGroupItem(toast: Toast<unknown>) {
    toast.visible = false;
    this.cdr.markForCheck();
    if (this.visibleToasts.length === 0 && this.isExpanded) {
      this.toggleToastGroup();
    } else {
      this.emiHeightWithGroup(this.isExpanded);
    }
  }

  afterClosedGroupItem(closeToast: HotToastClose) {
    const toastIndex = this.groupChildrenToasts.findIndex((t) => t.id === closeToast.id);
    if (toastIndex > -1) {
      this.groupChildrenToastRefs = this.groupChildrenToastRefs.filter((t) => t.getToast().id !== closeToast.id);
      this.cdr.markForCheck();
    }
  }

  toggleToastGroup() {
    const event = this.isExpanded ? 'collapse' : 'expand';
    this.toggleGroup.emit({
      byAction: true,
      event,
      id: this.toast().id,
    });
    this.emiHeightWithGroup(event === 'expand');
  }

  private emiHeightWithGroup(isExpanded: boolean) {
    if (isExpanded) {
      const rafId = requestAnimationFrame(() => {
        this.rafIds = this.rafIds.filter((id) => id !== rafId);
        if (this.destroyed) return;
        this.height.emit((this.nativeElement()?.offsetHeight || 0) + (this.groupHeight || 0));
      });
      this.rafIds.push(rafId);
    } else {
      const rafId = requestAnimationFrame(() => {
        this.rafIds = this.rafIds.filter((id) => id !== rafId);
        if (this.destroyed) return;
        this.height.emit(this.nativeElement().offsetHeight);
      });
      this.rafIds.push(rafId);
    }
  }
}
